/**
 * The captain's words typed out (bram/bramSpeech.ts paces them). The text element keeps the whole line from the
 * start, so the bubble's size, its placement and what VoiceOver reads never change while it types; the full line is
 * only hidden (`typing`) under an aria-hidden copy that shows the part said so far. The copy wraps exactly like the
 * line beneath it: it is the same text in the same box, with the unsaid rest invisible rather than missing.
 */
import { markSegments, plainText, type GuideKind } from "./guide";
import { Speech, SpeechPaces, sharedPrefix, speechTimeline } from "./bram/bramSpeech";
import { el, setMarkedText } from "./guideWidgets";
import { motionOk } from "../kit";

export class Typewriter {
  private speech: Speech | null = null;
  private copy: HTMLElement | null = null;
  private shown = -1;
  private raf = 0;

  /** `onSpeech` hears each new line (the live captain's mouth follows it) and null when it is over. */
  constructor(private readonly target: HTMLElement, private readonly kind: GuideKind, private readonly onSpeech: (s: Speech | null) => void, private readonly onDone: () => void = () => {}) {}

  /** Still typing (the first tap on an info line finishes it instead of continuing). */
  get running(): boolean { return this.speech !== null && !this.speech.done(performance.now()); }

  /**
   * A line to type. The same step's line changing (one that follows the player) types on from where it stops matching
   * the old one; `fresh` (the next line of a replay) types from the start.
   */
  set(text: string, fresh = false): void {
    const before = this.target.dataset.text;
    if (before === text) return;
    setMarkedText(this.target, text);
    this.copy = null;
    const plain = plainText(text);
    if (!motionOk() || plain.length === 0) { this.end(); return; }
    const from = before && !fresh ? sharedPrefix(plainText(before), plain) : 0;
    this.speech = new Speech(speechTimeline(plain, SpeechPaces[this.kind], from), performance.now());
    this.onSpeech(this.speech);
    this.shown = -1;
    this.target.classList.add("typing");
    this.copy = el("span", "g-type");
    this.copy.setAttribute("aria-hidden", "true");
    this.target.append(this.copy);
    this.tick();
  }

  /** Shows the rest at once (the player's first tap); the mouth shuts. */
  finish(): void {
    if (!this.speech) return;
    this.speech.finish(performance.now());
    this.tick();
  }

  stop(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.speech = null;
    this.copy?.remove();
    this.copy = null;
    this.target.classList.remove("typing");
  }

  private end(): void {
    const had = this.speech !== null || this.copy !== null;
    this.stop();
    this.onSpeech(null);
    if (had) this.onDone();
  }

  private tick = (): void => {
    this.raf = 0;
    const sp = this.speech, copy = this.copy;
    if (!sp || !copy) return;
    const now = performance.now();
    if (sp.done(now)) { this.end(); return; }
    const n = sp.revealed(now);
    if (n !== this.shown) { this.shown = n; this.draw(copy, n); }
    this.raf = requestAnimationFrame(this.tick);
  };

  /** The line with its first `n` characters visible and the rest laid out but invisible. */
  private draw(copy: HTMLElement, n: number): void {
    const parts: Node[] = [];
    let at = 0;
    for (const seg of markSegments(this.target.dataset.text ?? "")) {
      const cut = Math.max(0, Math.min(seg.text.length, n - at));
      const said = seg.text.slice(0, cut), rest = seg.text.slice(cut);
      const wrap = (t: string) => (seg.bold ? el("b", "", t) : document.createTextNode(t));
      if (said) parts.push(wrap(said));
      if (rest) { const u = el("span", "g-un"); u.append(wrap(rest)); parts.push(u); }
      at += seg.text.length;
    }
    copy.replaceChildren(...parts);
  }
}
